import axios from "axios";
import { getCookie } from "goi_common";

export const Axios = axios.create({
  withCredentials: true,
  baseURL: process.env.NEXT_PUBLIC_BASE_URL,
});

/**
 * 요청 직전에 access 토큰을 되살린다. 인증 여부로 동작이 갈리는 요청 앞에 붙인다.
 *
 * 서버는 access 토큰만으로 회원/비회원을 판정하고, 없거나 무효면 401이 아니라
 * **익명으로 처리**한다. 그래서 access가 만료된 회원이 그대로 제출하면 비회원 견적이
 * 새로 생기고, 로그인을 다시 요구받는다.
 *
 * access 쿠키는 httpOnly라 JS가 못 읽는다. 대신 같은 응답에서 같은 TTL(2h)로 심기는
 * `is_logged_in` 플래그 쿠키를 프록시로 쓴다. 플래그가 없으면 (a) 만료된 회원이거나
 * (b) 진짜 비회원인데, refresh 토큰은 읽을 수 없어 구분이 불가능하다. 그래서 일단
 * 시도하고 실패는 삼킨다 — (a)면 복구되고 (b)면 400을 받고 그대로 비회원으로 진행한다.
 *
 * platform 앱의 `utils/axios.ts`에 같은 함수가 있다. 별개 앱이라 Axios 인스턴스를
 * 공유할 수 없어 복제한다. 한쪽을 고치면 다른 쪽도 함께 봐야 한다.
 */
export const ensureFreshAuth = async (): Promise<boolean> => {
  const isLoggedInCookieKey = process.env.NEXT_PUBLIC_IS_LOGGED_IN_COOKIE;
  if (!isLoggedInCookieKey || getCookie(isLoggedInCookieKey) === "true")
    return false;

  try {
    await Axios.post("/users/refresh/");
    return true;
  } catch {
    return false;
  }
};

Axios.interceptors.response.use(
  (res) => {
    return res;
  },
  async (err) => {
    const originalRequest = err.config;

    if (
      err.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/users/refresh/")
    ) {
      originalRequest._retry = true;
      try {
        await Axios.post("/users/refresh/");
        return Axios(originalRequest);
      } catch {}
    }

    return Promise.reject(err);
  }
);
